import { StyledProps } from '@front10';
import { useTranslation } from '@front10/utils';
import VisuallyHidden from '@reach/visually-hidden';
import React from 'react';
import { Box } from '@front10/base-elements';

interface Props {
  /**
   * Formatted Total Price.
   */
  formattedTotalPrice: string;
  /**
   * Currency Code
   */
  currencyCode?: string;
  /**
   * Currency Code
   */
  currencySymbol?: string;
  /**
   * Show price promo.
   */
  showPricePromo?: boolean;
  /**
   * Show currency code.
   */
  showCurrencyCode?: boolean;
  /**
   * Show currency symbol.
   */
  showCurrencySymbol?: boolean;
  /**
   * Show asterisk
   */
  showPriceDisclaimer?: boolean;
  /**
   * Show price extra info
   */
  showPriceExtraInfo?: boolean;
  /**
   * Custom label for pricePromo
   */
  pricePromoLabel?: string;
  /**
   * Custom label for extraInfo
   */
  extraInfoLabel?: string;
  /**
   * Define if show the currency symbol as a <sup> or <span> tag
   */
  showCurrencySymbolSub?: boolean;
  /**
   * Currency value to display
   */
  currencyToDisplay?: string;
  /**
   * Define in what order should be displayed the currency
   */
  currencyInFront?: boolean;
}

// currencyToDisplay atom just render whatever you pass: "Dog"
const renderCurrencyToDisplay = (currencyToDisplay: string) => <span>{currencyToDisplay}</span>;

function FareItemPrice(props: StyledProps<Props>) {
  const {
    formattedTotalPrice = '',
    currencyCode = 'USD',
    currencySymbol = '$',
    showPricePromo = true,
    showPriceExtraInfo = false,
    showCurrencyCode = true,
    showCurrencySymbol = true,
    showPriceDisclaimer = true,
    showCurrencySymbolSub = true,
    pricePromoLabel = 'cmp-price-from-label',
    extraInfoLabel = 'cmp-price-extra-info',
    currencyToDisplay = '',
    currencyInFront = true,
    className
  } = props;

  const tag = showCurrencySymbolSub ? 'sup' : 'span';
  const Tag = tag;
  const translate = useTranslation();

  const currency = (
    <>
      {showCurrencyCode && <span data-fr-currency-code>{currencyCode}</span>}
      {showCurrencySymbol && <Tag data-fr-currency-symbol>{currencySymbol}</Tag>}
    </>
  );

  return (
    <Box data-fr-item-price className={className} variant="text.fontSize7">
      {showPricePromo && <span data-fr-promo-label>{translate(pricePromoLabel, 'From')}</span>}
      <Box data-fr-currency sx={{ display: 'flex', color: 'primary' }} variant="text.fontSize3">
        {/* currencyToDisplay in Front */}
        {currencyToDisplay && currencyInFront
          ? renderCurrencyToDisplay(currencyToDisplay)
          : currencyToDisplay === '' && currencyInFront && currency}
        <span data-atom-price>{formattedTotalPrice}</span>
        {/* currencyToDisplay in Back */}
        {currencyToDisplay && !currencyInFront
          ? renderCurrencyToDisplay(currencyToDisplay)
          : currencyToDisplay === '' && !currencyInFront && currency}
        {showPriceDisclaimer && (
          <>
            <span aria-hidden="true">*</span>
            <VisuallyHidden>
              {' '}
              {translate('cmp-price-disclaimer', 'Price subject to changes')}
            </VisuallyHidden>
          </>
        )}
      </Box>
      {showPriceExtraInfo ? (
        <span data-fr-extra-info>{translate(extraInfoLabel, 'per person')}</span>
      ) : null}
    </Box>
  );
}

export default FareItemPrice;
